import {useState} from 'react';
import styled from 'styled-components';
import {useDispatch} from 'react-redux';
import {updateShowDisclaimer} from '../../../slices/application';
import appConfig from '../../../config';
import DisclaimerModal from '../../Modal/DisclaimerModal';
import '../../Buttons/Buttons.scss';
import {useTheme} from '../../../contexts/ThemeContext';

/**
 * OrderConfirm - Order confirmation page component.
 * Displays order summary and handles submission via callbacks.
 *
 * @param {Object} props
 * @param {Object} props.confirmState - State from ChooseQuantity (designID, product, price, etc.)
 * @param {function} props.onCancel - Callback when cancel is clicked (go back to editor)
 * @param {function} props.onOrderSubmit - Callback when order is submitted.
 *   Signature: (orderData: object) => void
 */
const OrderConfirm = ({confirmState, onCancel, onOrderSubmit}) => {
  const theme = useTheme();
  const [state] = useState(confirmState);
  const [isDisabled, setIsDisabled] = useState(true);
  const [submitDisabled, setsubmitDisabled] = useState(false);
  const dispatch = useDispatch();
  const cdnUri = appConfig.cdnUri;

  const Cancel = () => {
    if (onCancel) {
      onCancel();
    }
  };

  const [checkedState, setCheckedState] = useState([false, false]);

  const handleOnChange = (position) => {
    const updatedCheckedState = checkedState.map((item, index) =>
      index === position ? !item : item
    );

    setCheckedState(updatedCheckedState);

    if (updatedCheckedState.every((e) => e)) {
      setIsDisabled(false);
    } else {
      setIsDisabled(true);
    }
  };

  function showDisclaimer() {
    dispatch(updateShowDisclaimer(true));
  }

  function sendCartDataToParent() {
    // disable submit button while sending
    setIsDisabled(true);
    // also disable the checkboxes and cancel
    setsubmitDisabled(true);
    const chosenDesign = {
      designID: state.designID,
      product: state.product,
      comment: state.orderInstructions,
      quantity: state.quantity,
      cartThumbnailUrl: `${cdnUri}/${state.cartThumbnail}`,
      printColor: state.printColor,
      cutLength: state.cutLength,
      unitPrice: state.perPiece?.replace('$', '')
    };
    onOrderSubmit?.(chosenDesign);
    localStorage.removeItem('lastDesign');
  }

  if (!state) {
    return <div>No order data available</div>;
  }

  return (
    <Confirm>
      <main className="hs-page hs-lab printing">
        <div className="container hs-page-container center-text">
          <h1 className="confirm-header h-style">CONFIRM ORDER</h1>
          <section className="hsp-preview">
            <img
              src={cdnUri + '/' + state.cartThumbnail + '?' + state.time}
              alt="preview"
            />
            <p className="desctxt">{state.description}</p>
          </section>
          <div className="boxspace">
            <h2 className="ordertxt">Total Price: {state.price}</h2>
            <h3 className="ordertxt">
              Quantity: {state.quantity} / {state.perPiece} ea
            </h3>
          </div>

          <div className="checkboxes">
            <div className="input-group">
              <input
                type="checkbox"
                id="federated-disclaimer1"
                name="disclaimer"
                value="disclaimer1"
                checked={checkedState[0]}
                disabled={submitDisabled}
                onChange={() => handleOnChange(0)}
              />
              <label htmlFor="federated-disclaimer1">
                I understand {theme.name}&apos;s{' '}
                <span className="hypertext" onClick={(e) => { e.preventDefault(); showDisclaimer(); }}>
                  Heatshrink Printing Disclaimer
                </span>
              </label>
            </div>
            <div className="input-group">
              <input
                type="checkbox"
                id="federated-disclaimer2"
                name="disclaimer"
                value="disclaimer2"
                checked={checkedState[1]}
                disabled={submitDisabled}
                onChange={() => handleOnChange(1)}
              />
              <label htmlFor="federated-disclaimer2">
                I understand that the visual print illustrated above is the
                digital proof for my order, i.e. &quot;What you see is what you get&quot;,
                that exact colors may vary, that any print not completely
                visible on one side of the tubing will not wrap to the other
                side, and that only one side of the tubing will be printed on. I
                also understand that no changes can be made to the heatshrink
                tubing and print once the design has been submitted and approved
                by the processing team and moved to the production queue.
              </label>
            </div>
          </div>

          <div className="boxspace">
            <button
              id="federatedSubmitBtn"
              type="button"
              className="btn btn-success btn-sm input-radius"
              disabled={isDisabled}
              onClick={sendCartDataToParent}>
              Add Heatshrink to Cart
            </button>
            <button
              type="button"
              className="btn btn-outline-primary btn-sm input-radius"
              id="federated-cancel-confirm"
              disabled={submitDisabled}
              onClick={Cancel}>
              Cancel
            </button>
          </div>
        </div>
        <DisclaimerModal />
      </main>
    </Confirm>
  );
};

export default OrderConfirm;

const Confirm = styled.div`
  button.btn {
    width: 240px;
    margin: 10px auto;
    display: block;
  }

  .boxspace {
    margin: 25px 0 40px;
  }

  .hs-page-container.center-text,
  .center-text {
    text-align: center;
  }

  .checkboxes {
    margin-bottom: 25px;
  }

  .confirm-header {
    margin: 20px auto 30px;
    padding: 0;
  }

  .desctext {
    color: var(--hsp-header-text);
    font-size: 1rem;
    text-transform: uppercase;
    font-family:
      'Montserrat',
      -apple-system,
      BlinkMacSystemFont,
      'Segoe UI',
      Roboto,
      'Helvetica Neue',
      Arial,
      'Noto Sans',
      sans-serif,
      'Apple Color Emoji',
      'Segoe UI Emoji',
      'Segoe UI Symbol',
      'Noto Color Emoji';
    margin: auto 20px;
  }

  .h-style {
    color: var(--hsp-header-text);
    font-family:
      'Montserrat',
      -apple-system,
      BlinkMacSystemFont,
      'Segoe UI',
      Roboto,
      'Helvetica Neue',
      Arial,
      'Noto Sans',
      sans-serif,
      'Apple Color Emoji',
      'Segoe UI Emoji',
      'Segoe UI Symbol',
      'Noto Color Emoji';
    font-weight: 700;
  }

  .hsp-preview {
    background-color: #f8f8f8;
    border: 2px solid var(--hsp-border-color);
    display: block;
    margin: 0 auto;
    position: relative;
    width: 100%;
    max-width: 800px;
    min-width: fit-content;
    overflow-y: clip;
    padding-bottom: 10px;
    img {
      border-bottom: #f8f8f8 1px solid;
      max-height: 330px;
      height: auto;
      max-width: 100%;

      padding: 30px;
    }
  }

  .hypertext {
    cursor: pointer;
    color: #337ab7;
  }

  .input-group {
    margin: 6px 0;
    input {
      margin: 5px 6px 0 0;
      align-self: self-start;
      width: 18px;
      height: 18px;
      cursor: pointer;
    }

    label {
      text-align: left;
      display: inline-block;
      width: 90%;
      cursor: pointer;
    }
  }

  .ordertxt {
    color: var(--hsp-header-text);
    font-weight: 400;
  }

  .btn-outline-primary {
    border-color: var(--hsp-btn-outline-border);
    color: var(--hsp-btn-outline-text);
    background-color: var(--hsp-btn-outline-bg);
    &:hover {
      border-color: var(--hsp-btn-outline-hover-border);
      background-color: var(--hsp-btn-outline-hover-bg) !important;
      color: var(--hsp-btn-outline-hover-text);
    }
    &:active,
    &:focus {
      border-color: var(--hsp-btn-outline-hover-border);
      background-color: var(--hsp-btn-outline-hover-bg) !important;
      color: var(--hsp-btn-outline-hover-text);
    }
  }
`;
